import { Box, SxProps, Typography } from '@mui/material';
import { CommercialIcon, EngineeringIcon, LinearChartIcon, StaffingIcon, Tooltip } from 'framework/ui/mui-builder';
import React from 'react';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { ZONE_CONFIG } from 'domain/shared/newProject';
import { ControlIssue } from './controlIssues';

type ControlIssueIconKey = ControlIssue['key'];

type ControlIssueIconConfig = {
  Icon: React.ComponentType<SvgIconProps>;
  title: string;
};

const CONTROL_ISSUE_ICON_CONFIG: Record<ControlIssueIconKey, ControlIssueIconConfig> = {
  commercial: {
    Icon: CommercialIcon,
    title: 'Commercial',
  },
  projectManagement: {
    Icon: LinearChartIcon,
    title: 'Project management',
  },
  engineering: {
    Icon: EngineeringIcon,
    title: 'Engineering',
  },
  staffing: {
    Icon: StaffingIcon,
    title: 'Staffing',
  },
};

const sharedIconSx: SxProps = {
  fontSize: '1.3rem',
  stroke: 'currentColor',
};

type ControlIssueIconsProps = {
  issues: ControlIssue[];
  variant?: 'icons' | 'rows';
  sx?: SxProps;
};

const ControlIssueIcons = ({ issues, variant = 'rows', sx }: ControlIssueIconsProps) => {
  if (variant === 'icons') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ...sx }}>
        {issues.map(issue => {
          const { Icon, title } = CONTROL_ISSUE_ICON_CONFIG[issue.key];
          const zoneConfig = ZONE_CONFIG[issue.zone];
          const tooltipContent = (
            <>
              <Typography variant="body2" mb={1}>
                {issue.message}
              </Typography>
              {issue.subIssues.length > 0 && (
                <Typography variant="body2" mb={1}>
                  <Typography component="span" variant="subtitle2">
                    {issue.subIssues.length}
                  </Typography>{' '}
                  {issue.subIssues.length === 1 ? 'issue' : 'issues'}
                </Typography>
              )}
              {issue.subIssues.length > 0 && (
                <ul>
                  {issue.subIssues.map(sub => (
                    <li key={sub.label}>
                      <strong>{sub.label}</strong>
                      {sub.description && ` ${sub.description}`}
                    </li>
                  ))}
                </ul>
              )}
            </>
          );

          return (
            <Tooltip key={issue.key} title={tooltipContent}>
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
                aria-label={title}
              >
                <Icon color={zoneConfig.color as SvgIconProps['color']} sx={sharedIconSx} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  return (
    <>
      {issues.map(issue => {
        const { Icon } = CONTROL_ISSUE_ICON_CONFIG[issue.key];
        const zoneConfig = ZONE_CONFIG[issue.zone];

        return (
          <Box key={issue.key} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, ...sx }}>
            <Icon color={zoneConfig.color as SvgIconProps['color']} sx={sharedIconSx} />
            <Typography color={zoneConfig.color}>{issue.message}</Typography>
          </Box>
        );
      })}
    </>
  );
};

export default ControlIssueIcons;
